import type { Scene } from "@babylonjs/core/scene";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { ZoneState } from "@arena/shared";
import { SALT_SHEET } from "./generatedGandhiArt.ts";

/** Persistent authoritative salt field: also visible to late joining players. */
export class SaltZoneView {
  private readonly mesh;
  private readonly material: StandardMaterial;
  private readonly texture: Texture;
  private age = 0;

  constructor(scene: Scene) {
    this.mesh = CreatePlane("salt-zone", { size: 1 }, scene);
    this.mesh.rotation.x = Math.PI / 2;
    this.mesh.isPickable = false;
    this.texture = new Texture(SALT_SHEET.file, scene, true, true, Texture.NEAREST_SAMPLINGMODE);
    this.texture.hasAlpha = true;
    this.texture.wrapU = this.texture.wrapV = Texture.CLAMP_ADDRESSMODE;
    this.texture.uScale = 1 / SALT_SHEET.cols;
    this.texture.vScale = 1 / SALT_SHEET.rows;
    this.material = new StandardMaterial("salt-zone", scene);
    this.material.diffuseTexture = this.texture;
    this.material.useAlphaFromDiffuseTexture = true;
    this.material.emissiveColor = new Color3(1, 1, 1);
    this.material.disableLighting = true;
    this.material.backFaceCulling = false;
    this.mesh.material = this.material;
  }

  update(zone: ZoneState, dt: number): void {
    this.age += dt;
    this.mesh.position.set(zone.x, zone.y + 0.075, zone.z);
    this.mesh.scaling.set(zone.radius * 2, zone.radius * 2, 1);
    const frame = Math.floor(this.age * SALT_SHEET.fps) % SALT_SHEET.frames;
    this.texture.uOffset = (frame % SALT_SHEET.cols) / SALT_SHEET.cols;
    this.texture.vOffset = 1 - (Math.floor(frame / SALT_SHEET.cols) + 1) / SALT_SHEET.rows;
    this.material.alpha = Math.min(1, this.age / 0.1, zone.ticks / 8);
  }

  dispose(): void {
    this.mesh.dispose(); this.material.dispose(); this.texture.dispose();
  }
}
